import React, { useEffect, useState, useMemo } from "react";
import { useDispatch } from "react-redux";
import { Images } from "../../../../../../utils/imageConstants";
import MatchSummary from "./MatchSummary";
import MatchesScoreCard from "./MatchesScoreCard";
import MatchesCommentary from "./MatchesCommentary";
import MatchesPlayers from "./MatchesPlayers";
import MatchesStreamer from "./MatchesStreamer";
import MatchesAnalysis from "./matchAnalysis/MatchesAnalysis";
import UpcomingMatchPointsTableTab from "./upcomingMatchesDetails/UpcomingMatchPointsTableTab";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import type { IMatchCommentary, IMatchDetails, IMatchOfficial, InningScorecard, IPlayersTeamSquad, ISubsitutes, ITeamsSquad, ITopBatters, ITopBowlers } from "./types/matchDetails.d";
import { QUERY_KEY, getLastNoversSummaryLatestQueryParams } from "../../../../../../services/queryEndPoint";
import { Api } from "../../../../../../services/interceptor";
// import { setActiveTab } from "../../../../../../redux/slice/activeTabSlice";
import { formatPrettyDateAndTime, getPlayerImageUrl, getTimeUntilMatchDetailed, getTeamScoresFromMatchDetails, resolvePublicImageUrl } from "../../../../../../helper/helper";
import { getMatchPersonDisplayName, getMatchVenueText } from "./matchDetailDisplay";

const ALL_TAB_LABELS = [
  "Summary",
  "Scorecard",
  "Commentary",
  // "Analysis",
  "Players",
  // "Streamer",
];

const UPCOMING_TAB_LABELS = ["Summary", "Squads", "Table"];

/** Short polling while a match is live (runs/scorecard/commentary stay fresh). */
const LIVE_MATCH_POLL_MS = 30_000;

const normalizePersonKey = (first?: string | null, last?: string | null) =>
  [first, last]
    .map((p) => (p ?? "").trim().toLowerCase())
    .filter((p) => p && p !== "null" && p !== "undefined")
    .join(" ");

const resolveTopPerformerPhoto = (player?: { image?: string | null; imageUrl?: string | null } | null): string | undefined => getPlayerImageUrl(player?.imageUrl) || getPlayerImageUrl(player?.image);

const allSquadMembers = (squad?: IPlayersTeamSquad): Array<ITeamsSquad | ISubsitutes> => [...(squad?.playingSquad ?? []), ...(squad?.bench ?? [])];

const resolvePlayerOfTheMatchAwardPhoto = (potm: IMatchDetails["playerOfTheMatch"] | undefined, topBatter: ITopBatters | undefined, topBowler: ITopBowlers | undefined, teamOnePlayers?: IPlayersTeamSquad, teamTwoPlayers?: IPlayersTeamSquad): string | undefined => {
  if (potm && typeof potm === "object") {
    const o = potm as IMatchOfficial;
    const direct = getPlayerImageUrl(o.imageUrl) || getPlayerImageUrl(o.imageUri) || getPlayerImageUrl(o.image);
    if (direct) return direct;

    const id = o.id?.trim();
    if (id) {
      if (topBatter?.player?.playerId === id) return resolveTopPerformerPhoto(topBatter.player);
      if (topBowler?.player?.playerId === id) return resolveTopPerformerPhoto(topBowler.player);
      for (const member of [...allSquadMembers(teamOnePlayers), ...allSquadMembers(teamTwoPlayers)]) {
        if (member.playerId === id) {
          const u = getPlayerImageUrl(member.imageUri);
          if (u) return u;
        }
      }
    }

    const nameKey = normalizePersonKey(o.playerFirstName, o.playerLastName);
    if (nameKey) {
      for (const p of [topBatter?.player, topBowler?.player]) {
        if (p && normalizePersonKey(p.playerFirstName, p.playerLastName) === nameKey) {
          return resolveTopPerformerPhoto(p);
        }
      }
      for (const member of [...allSquadMembers(teamOnePlayers), ...allSquadMembers(teamTwoPlayers)]) {
        if (normalizePersonKey(member.playerFirstName, member.playerLastName) === nameKey) {
          const u = getPlayerImageUrl(member.imageUri);
          if (u) return u;
        }
      }
    }
  }

  if (typeof potm === "string") {
    const trimmed = potm.trim().toLowerCase();
    if (trimmed && trimmed !== "-" && trimmed !== "null" && trimmed !== "undefined") {
      for (const p of [topBatter?.player, topBowler?.player]) {
        if (p && `${p.playerFirstName ?? ""} ${p.playerLastName ?? ""}`.trim().toLowerCase() === trimmed) {
          return resolveTopPerformerPhoto(p);
        }
      }
      for (const member of [...allSquadMembers(teamOnePlayers), ...allSquadMembers(teamTwoPlayers)]) {
        if (`${member.playerFirstName ?? ""} ${member.playerLastName ?? ""}`.trim().toLowerCase() === trimmed) {
          const u = getPlayerImageUrl(member.imageUri);
          if (u) return u;
        }
      }
    }
  }

  return undefined;
};

const MatchesDetails = () => {
  // const dispatch = useDispatch();
  const navigate = useNavigate();
  const { matchId } = useParams();
  const { state } = useLocation();
  const [matchTab, setMatchTab] = useState("Summary");
  const tournamentId = sessionStorage.getItem("tournamentId") || undefined;

  // useEffect(() => {
  //   dispatch(setActiveTab(null));
  // }, [dispatch]);

  useEffect(() => {
    if (state?.matchTab) {
      setMatchTab(state.matchTab);
      // Smoothly scroll to the tabs section so users land at the tablist
      setTimeout(() => {
        const el = document.getElementById("match-tabs");
        el?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 0);
    }
  }, [state?.matchTab]);

  const { isLoading, data: matchSummary } = useQuery<IMatchDetails>({
    queryKey: [QUERY_KEY.getMatchInfo, matchId],
    queryFn: async () => {
      const res = await Api.get(`${QUERY_KEY.getMatchInfo}/${matchId}`);
      return res.data;
    },
    refetchInterval: (query) => {
      const d = query.state.data;
      return d?.matchStatus === "IN_PROGRESS" ? LIVE_MATCH_POLL_MS : false;
    },
  });

  const { isLoading: scoreCardLoading, data: scores } = useQuery<InningScorecard[]>({
    queryKey: [QUERY_KEY.getScoreByMatch, matchId],
    queryFn: async () => {
      const res = await Api.get(`${QUERY_KEY.getScoreByMatch}/${matchId}`);
      return res?.data?.inningScoreCardList;
    },
    refetchInterval: matchSummary?.matchStatus === "IN_PROGRESS" ? LIVE_MATCH_POLL_MS : false,
  });

  const commentaryInningId = useMemo(() => {
    if (!scores?.length) return "";
    if (matchSummary?.matchStatus === "IN_PROGRESS") {
      const inProgress = scores.find((inning) => inning.inningStatus === "IN_PROGRESS");
      return (inProgress ?? scores[scores.length - 1])?.inningId ?? "";
    }
    return scores[scores.length - 1]?.inningId ?? scores[0]?.inningId ?? "";
  }, [scores, matchSummary?.matchStatus]);

  const { isLoading: commentaryLoading, data: commentaryData } = useQuery<IMatchCommentary[]>({
    queryKey: [QUERY_KEY.getCommentary, matchId, commentaryInningId],
    queryFn: async () => {
      const res = await Api.get(`${QUERY_KEY.getCommentary}`, {
        params: {
          inningId: commentaryInningId,
          ...getLastNoversSummaryLatestQueryParams,
        },
      });
      return res?.data?.overSummaryList;
    },
    enabled: !!matchId && !!commentaryInningId,
    refetchInterval: matchSummary?.matchStatus === "IN_PROGRESS" ? LIVE_MATCH_POLL_MS : false,
  });

  const { isLoading: isTeamOneLoading, data: teamOnePlayers } = useQuery<IPlayersTeamSquad>({
    queryKey: [QUERY_KEY.getTeamSquadForMatch, matchId, matchTab, matchSummary?.teamOne?.teamSnapshotId],
    queryFn: async () => {
      const res = await Api.get(`${QUERY_KEY.getTeamSquadForMatch}/${matchId}/${matchSummary?.teamOne?.teamSnapshotId}`);
      return res?.data || [];
    },
  });
  // const teamOnePlayers: ITeamsSquad[] = teamOne?.playingSquad ?? [];
  // const teamOnePlayersSubstitues: ISubsitutes[] = teamOne?.bench ?? [];

  const { isLoading: isTeamTwoLoading, data: teamTwoPlayers } = useQuery<IPlayersTeamSquad>({
    queryKey: [QUERY_KEY.getTeamSquadForMatch, matchId, matchTab, matchSummary?.teamTwo?.teamSnapshotId],
    queryFn: async () => {
      const res = await Api.get(`${QUERY_KEY.getTeamSquadForMatch}/${matchId}/${matchSummary?.teamTwo?.teamSnapshotId}`);
      return res?.data;
    },
  });

  const matchStatus = matchSummary?.matchStatus;
  const isUpcoming = matchStatus === "UPCOMING" || matchStatus === "SCHEDULED";
  const venueText = useMemo(() => getMatchVenueText(matchSummary), [matchSummary]);
  const playerOfTheMatchName = useMemo(() => getMatchPersonDisplayName(matchSummary?.playerOfTheMatch), [matchSummary?.playerOfTheMatch]);
  const matchSummaryResultText = useMemo(() => matchSummary?.matchResult?.trim() || matchSummary?.equation?.trim() || matchSummary?.tossResult?.trim() || "-", [matchSummary?.equation, matchSummary?.matchResult, matchSummary?.tossResult]);
  const matchSummaryTitle = useMemo(() => matchSummary?.leagueName?.trim() || matchSummary?.tournament?.tournamentName?.trim() || "-", [matchSummary?.leagueName, matchSummary?.tournament?.tournamentName]);
  const awardCards = useMemo(() => {
    const topBatter = matchSummary?.topBatters?.[0];
    const topBowler = matchSummary?.topBowlers?.[0];
    // const playerOfTheMatch = matchSummary?.playerOfTheMatch;

    // const playerOfMatchDetail = topBatter ? `${topBatter.runs} runs | SR ${Number(topBatter.strikeRate ?? 0).toFixed(1)}` : matchSummaryResultText;

    const playerOfTheMatchPhoto = resolvePlayerOfTheMatchAwardPhoto(matchSummary?.playerOfTheMatch, topBatter, topBowler, teamOnePlayers, teamTwoPlayers);

    if (playerOfTheMatchName != null && playerOfTheMatchName !== "-" && playerOfTheMatchName.toLowerCase() !== "null" && playerOfTheMatchName.toLowerCase() !== "undefined" && playerOfTheMatchName.trim() !== "") {
      return [
        {
          key: "player-of-the-match",
          label: "Player Of The Match",
          name: playerOfTheMatchName,
          detail: null,
          imageUri: playerOfTheMatchPhoto,
        },
      ];
    }
    return [];
  }, [matchSummary?.playerOfTheMatch, matchSummary?.topBatters, matchSummary?.topBowlers, matchSummaryResultText, playerOfTheMatchName, teamOnePlayers, teamTwoPlayers]);

  // Filter tabs based on match status
  const TAB_LABELS = isUpcoming ? UPCOMING_TAB_LABELS : ALL_TAB_LABELS;
  const timeUntilMatch = useMemo(() => {
    if (isUpcoming && matchSummary?.startDate) {
      return getTimeUntilMatchDetailed(matchSummary.startDate);
    }
    return null;
  }, [isUpcoming, matchSummary?.startDate]);

  // Used for over/ball display — sourced from the scorecard (getScoreByMatch) which
  // is polled every 30s and carries the current over totals + lastOverBalls.
  const currentInning = matchStatus === "IN_PROGRESS" && scores?.length ? (scores.find((inning) => inning.inningStatus === "IN_PROGRESS") ?? scores[scores.length - 1]) : null;

  // Used for batting/bowling player stats — sourced from matchSummary (getMatchInfo)
  // which is also polled every 30s and returns the full battingScoreCardResponseList
  // and bowlingScoreCardResponses that getScoreByMatch does not populate.
  const liveInning = useMemo(() => {
    const list = matchSummary?.inningScorecardList;
    if (matchStatus !== "IN_PROGRESS" || !list?.length) return null;
    return list.find((i) => i.inningStatus === "IN_PROGRESS" || i.inningStatus === "STARTED") ?? list[list.length - 1];
  }, [matchStatus, matchSummary?.inningScorecardList]);

  // 1-based match inning number derived from position in inningScorecardList.
  const liveInningNumber = useMemo(() => {
    const list = matchSummary?.inningScorecardList;
    if (!list?.length || !liveInning) return 1;
    const idx = list.findIndex((i) => i.inningId === liveInning.inningId);
    return idx >= 0 ? idx + 1 : 1;
  }, [matchSummary?.inningScorecardList, liveInning]);

  const teamOneName = matchSummary?.teamOne?.teamName?.trim().toUpperCase();
  const teamTwoName = matchSummary?.teamTwo?.teamName?.trim().toUpperCase();
  const teamOneInning = teamOneName && scores?.length ? scores.find((i) => i.battingTeamName?.trim().toUpperCase() === teamOneName) : undefined;
  const teamTwoInning = teamTwoName && scores?.length ? scores.find((i) => i.battingTeamName?.trim().toUpperCase() === teamTwoName) : undefined;

  // isCurrentOverInProgress = true when balls have been bowled in the active over.
  // false = over just ended / between overs → show lastOverBalls instead.
  const isCurrentOverInProgress = !!(liveInning?.currentOverBalls && liveInning.currentOverBalls.length > 0);

  const latestOverBalls = useMemo(() => {
    // Over in progress → show balls delivered so far this over
    if (isCurrentOverInProgress && liveInning?.currentOverBalls) {
      return liveInning.currentOverBalls;
    }
    // Between overs → show the completed previous over's balls
    if (liveInning?.lastOverBalls && liveInning.lastOverBalls.length > 0) {
      return liveInning.lastOverBalls;
    }
    // Final fallback: latest over from commentary
    if (commentaryData && commentaryData.length > 0) {
      const latestCommentary = commentaryData[commentaryData.length - 1];
      const balls = latestCommentary?.ballsResponseList || [];
      if (balls.length > 0) return balls;
    }
    return [];
  }, [isCurrentOverInProgress, liveInning?.currentOverBalls, liveInning?.lastOverBalls, commentaryData]);

  const renderHeader = () => {
    if (matchStatus === "IN_PROGRESS") {
      return (
        <header className="background-image-wrapper live-match-header" role="banner" aria-label="Live match header">
          <div className="live-match-header-top">
            <div className="live-badge">
              <span className="live-dot"></span>
              <span className="live-text">LIVE</span>
            </div>
            <span className="match-date-time">{formatPrettyDateAndTime(matchSummary?.startDate ?? "-")}</span>
            <span className="header-dot">
              <img src={Images.match_details_dot} alt="" />
            </span>
            <span className="match-venue">{venueText}</span>
          </div>

          <div className="live-match-teams-section">
            <p className="innings-info">{liveInning ? `${liveInningNumber === 2 ? "Second" : "First"} Innings` : matchSummary?.tossResult || "-"}</p>

            <div className="live-teams-wrapper">
              <div className="live-team-left">
                <div className="live-team-info">
                  <div className="live-team-logo-wrapper">
                    <img src={Images.team_logo} alt={matchSummary?.teamOne?.teamName || "Team"} />
                  </div>
                  <p className="live-team-name">{matchSummary?.teamOne?.teamName?.toUpperCase() || "-"}</p>
                </div>
                {teamOneInning ? (
                  <div className="live-score-info">
                    <span className="live-score">
                      {teamOneInning.runs}/{teamOneInning.wicket}
                    </span>
                    <span className="live-over">
                      ({teamOneInning.over}.{teamOneInning.ball} OV)
                    </span>
                  </div>
                ) : (
                  <div className="live-score-info">
                    <span className="yet-to-bat">YET TO BAT</span>
                  </div>
                )}
              </div>

              <div className="live-divider-vertical"></div>

              <div className="live-team-right">
                {teamTwoInning ? (
                  <div className="live-score-info">
                    <span className="live-score">
                      {teamTwoInning.runs}/{teamTwoInning.wicket}
                    </span>
                    <span className="live-over">
                      ({teamTwoInning.over}.{teamTwoInning.ball} OV)
                    </span>
                  </div>
                ) : (
                  <div className="live-score-info">
                    <span className="yet-to-bat">YET TO BAT</span>
                  </div>
                )}
                <div className="live-team-info">
                  <div className="live-team-logo-wrapper">
                    <img src={Images.team_logo} alt={matchSummary?.teamTwo?.teamName || "Team"} />
                  </div>
                  <p className="live-team-name">{matchSummary?.teamTwo?.teamName?.toUpperCase() || "-"}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Current / Previous Over Display */}
          {currentInning && (
            <div className="current-over-section">
              <div className="over-display-wrapper">
                <div className="over-display">
                  <span className="over-label">{isCurrentOverInProgress ? `OVERS ${currentInning.over}.${currentInning.ball}` : `LAST OVER`}</span>
                  <div className="balls-row">
                    {latestOverBalls.slice(-7).map((ball: any, idx) => {
                      const isWicket = ball?.wicket?.wicket_id != null;
                      const runs = ball?.runs_scored || 0;
                      const isBoundary = runs === 4 || runs === 6;
                      const isDot = runs === 0 && !isWicket;
                      const isWide = ball?.extra?.type === "WIDE";
                      const isNoBall = ball?.extra?.type === "NO_BALL";

                      let ballClass = "runs-ball";
                      if (isWicket) {
                        ballClass = "wicket-ball";
                      } else if (isBoundary) {
                        ballClass = "boundary-ball";
                      } else if (isDot) {
                        ballClass = "dot-ball";
                      }

                      const displayValue = isWicket ? "W" : runs;

                      return (
                        <div key={ball?.ball_id ?? idx} className="ball-indicator-wrapper">
                          <div className={`ball-indicator ${ballClass}`}>
                            <span>{displayValue}</span>
                          </div>
                          {isWide && <span className="extra-indicator">WD</span>}
                          {isNoBall && <span className="extra-indicator">NB</span>}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Horizontal Divider */}
              <div className="over-section-divider"></div>

              {/* Batting and Bowling Info — sourced from liveInning
                  (matchSummary.inningScorecardList, polled every 30s). */}
              {(() => {
                // Only show STRIKER and NON_STRIKER — never show OUT players.
                const activeBatsmen = (liveInning?.battingScoreCardResponseList ?? []).filter((b) => b.status === "STRIKER" || b.status === "NON_STRIKER").sort((a, b) => (a.status === "STRIKER" ? -1 : 1));

                // The current bowler is the one who delivered the most recent ball
                // in currentOverBalls; fall back to the last entry in the list.
                const currentBowlerPlayerId = liveInning?.currentOverBalls?.[0]?.bowler?.playerId;
                const currentBowler = currentBowlerPlayerId
                  ? liveInning?.bowlingScoreCardResponses?.find(b => b.playerId === currentBowlerPlayerId)
                  : liveInning?.bowlingScoreCardResponses?.[liveInning.bowlingScoreCardResponses.length - 1];
                 
                return (
                  <div className="live-batting-bowling-section">
                    <div className="batting-section-live">
                      <p className="section-title">BATTING</p>
                      <div className="batsmen-list">
                        {activeBatsmen.map((batsman, idx) => (
                          <div key={batsman.playerId} className="batsman-card">
                            <div className="batsman-info">
                              <img src={Images.team_logo} alt={batsman.playerFirstName} className="batsman-image" />
                              <span className="batsman-name">
                                {batsman.playerFirstName} {batsman.playerLastName}
                              </span>
                              {batsman.status === "STRIKER" && <div className="bat-icon"></div>}
                            </div>
                            <span className="batsman-score">
                              <strong>{batsman.run}</strong> <span>({batsman.balls})</span>
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="live-divider-horizontal"></div>

                    <div className="bowling-section-live">
                      <p className="section-title">BOWLING</p>
                      {currentBowler && (
                        <div className="bowler-card">
                          <div className="bowler-info">
                            <img src={Images.team_logo} alt={currentBowler.playerFirstName} className="bowler-image" />
                            <span className="bowler-name">
                              {currentBowler.playerFirstName} {currentBowler.playerLastName}
                            </span>
                          </div>
                          <span className="bowler-figures">
                            <strong>
                              {currentBowler.wickets || 0}/{currentBowler.runs || 0}
                            </strong>{" "}
                            <span>({currentBowler.over})</span>
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}
               <div className="match-status-equation">
                      {matchSummary?.matchResult || matchSummary?.equation || "-"}
                    </div>
            </div>
          )}
        </header>
      );
    } else if (isUpcoming) {
      return (
        <header className="background-image-wrapper upcoming-match-header" role="banner" aria-label="Upcoming match header">
          <img
            src={Images.previous_icon}
            alt="back-button"
            onClick={() => {
              if (state?.returnTo) {
                navigate(state.returnTo);
              } else {
                navigate(-1);
              }
            }}
            className="back-button-upcoming"
          />
          <div className="upcoming-match-header-top">
            <span className="upcoming-status">UPCOMING</span>
            <span className="header-dot">
              <img src={Images.match_details_dot} alt="" />
            </span>
            <span className="match-date-time">{formatPrettyDateAndTime(matchSummary?.startDate ?? "-")}</span>
            <span className="header-dot">
              <img src={Images.match_details_dot} alt="" />
            </span>
            <span className="match-venue">{venueText}</span>
          </div>

          <div className="upcoming-teams-wrapper">
            <div className="upcoming-team-left">
              <div className="upcoming-team-logo-wrapper">
                <img src={resolvePublicImageUrl(matchSummary?.teamOne?.imageUrl) || Images.default_team_image} alt={matchSummary?.teamOne?.teamName || "Team"} />
              </div>
              <p className="upcoming-team-name">{matchSummary?.teamOne?.teamName?.toUpperCase() || "-"}</p>
            </div>

            <div className="upcoming-countdown">
              <p className="countdown-label">Match starts in :</p>
              <p className="countdown-time">{timeUntilMatch ? `${timeUntilMatch.days}d ${timeUntilMatch.hours}h ${timeUntilMatch.minutes}m` : "-"}</p>
            </div>

            <div className="upcoming-team-right">
              <div className="upcoming-team-logo-wrapper">
                <img src={resolvePublicImageUrl(matchSummary?.teamTwo?.imageUrl) || Images.default_team_image} alt={matchSummary?.teamTwo?.teamName || "Team"} />
              </div>
              <p className="upcoming-team-name">{matchSummary?.teamTwo?.teamName?.toUpperCase() || "-"}</p>
            </div>
          </div>
        </header>
      );
    } else {
      // ENDED state - current view
      const { firstTeamScore, secondTeamScore, firstTeamName, secondTeamName, showYetToBat } = getTeamScoresFromMatchDetails(matchSummary, scores);

      return (
        <header className="background-image-wrapper ended-match-summary-header" role="banner" aria-label="Match summary header background">
          <div className="ended-match-summary-shell">
            <div className="ended-match-summary-main">
              <button
                type="button"
                className="ended-match-back-button"
                onClick={() => {
                  if (state?.returnTo) {
                    navigate(state.returnTo);
                  } else {
                    navigate(-1);
                  }
                }}
                aria-label="Go back"
              >
                <img src={Images.white_back_arrow} alt="" />
              </button>

              <div className="ended-match-summary-content">
                <div className="ended-match-summary-meta">
                  <span className="result-chip">Result</span>
                  <span className="meta-text">
                    <time dateTime={matchSummary?.startDate ?? undefined}>{formatPrettyDateAndTime(matchSummary?.startDate ?? "-")}</time>
                  </span>
                </div>

                <h1 className="ended-match-summary-title">{matchSummaryTitle}</h1>
                <p className="ended-match-summary-venue">{venueText}</p>

                <section className="ended-match-team-list" aria-label="Team scores">
                  <article className="ended-match-team-row" aria-label={`${firstTeamName} score`}>
                    <div className="ended-match-team-identity">
                      <figure className="ended-match-team-logo">
                        <img src={Images.team_logo} alt={`${firstTeamName} team logo`} />
                      </figure>
                      <figcaption className="ended-match-team-name">{firstTeamName || "-"}</figcaption>
                    </div>
                    <div className="ended-match-team-score" aria-label={`${firstTeamName} score details`}>
                      {firstTeamScore ? (
                        <>
                          <span className="score-value" aria-label="Score">
                            {firstTeamScore.runs}/{firstTeamScore.wicket}
                          </span>
                          <span className="score-overs" aria-label="Overs">
                            ({firstTeamScore.over}.{firstTeamScore.ball} OV)
                          </span>
                        </>
                      ) : (
                        <span className="score-value" aria-label="Score">
                          -
                        </span>
                      )}
                    </div>
                  </article>

                  <article className="ended-match-team-row" aria-label={`${secondTeamName} score`}>
                    <div className="ended-match-team-identity">
                      <figure className="ended-match-team-logo">
                        <img src={Images.team_logo} alt={`${secondTeamName} team logo`} />
                      </figure>
                      <figcaption className="ended-match-team-name">{secondTeamName || "-"}</figcaption>
                    </div>
                    <div className="ended-match-team-score" aria-label={`${secondTeamName} score details`}>
                      {showYetToBat ? (
                        <span className="score-value" aria-label="Score">
                          Yet to Bat
                        </span>
                      ) : secondTeamScore ? (
                        <>
                          <span className="score-value" aria-label="Score">
                            {secondTeamScore.runs}/{secondTeamScore.wicket}
                          </span>
                          <span className="score-overs" aria-label="Overs">
                            ({secondTeamScore.over}.{secondTeamScore.ball} OV)
                          </span>
                        </>
                      ) : (
                        <span className="score-value" aria-label="Score">
                          -
                        </span>
                      )}
                    </div>
                  </article>
                </section>
              </div>
            </div>

            <aside className="ended-match-summary-awards" aria-label="Match awards">
              {awardCards.map((card) => (
                <article key={card.key} className="ended-match-award-card">
                  <img
                    src={card.imageUri || Images.player_ofTheMatch_image}
                    alt={card.name}
                    className="ended-match-award-image"
                    onError={(e) => {
                      const el = e.currentTarget;
                      if (el.src !== Images.player_ofTheMatch_image) {
                        el.src = Images.player_ofTheMatch_image;
                      }
                    }}
                  />
                  <div className="ended-match-award-content">
                    <p className="ended-match-award-label">{card.label}</p>
                    <p className="ended-match-award-name">{card.name || "-"}</p>
                    <p className="ended-match-award-detail">{card.detail}</p>
                  </div>
                </article>
              ))}
            </aside>
          </div>

          <p className="match-result" role="status" aria-live="polite">
            <span>{matchSummaryResultText}</span>
          </p>
        </header>
      );
    }
  };

  const renderContent = () => {
    switch (matchTab) {
      case "Summary":
        return <MatchSummary setMatchTab={setMatchTab} scores={scores} commentaryData={commentaryData} matchStatus={matchStatus} matchSummary={matchSummary} />;
      case "Scorecard":
        return (
          <div>
            <MatchesScoreCard matchSummary={matchSummary} scores={scores} teamOnePlayers={teamOnePlayers} teamTwoPlayers={teamTwoPlayers} />
          </div>
        );
      case "Commentary":
        return (
          <div>
            <MatchesCommentary scores={scores ?? []} matchStatus={matchStatus} activeCommentaryInningId={commentaryInningId} liveCommentarySnapshot={matchStatus === "IN_PROGRESS" ? commentaryData : undefined} />
          </div>
        );
      case "Analysis":
        return (
          <div>
            <MatchesAnalysis teamOnePlayers={teamOnePlayers} teamTwoPlayers={teamTwoPlayers} matchId={matchId} tournamentId={tournamentId} />
          </div>
        );
      case "Players":
      case "Squads":
        return (
          <div>
            <MatchesPlayers matchSummary={matchSummary} matchTab={matchTab} teamOnePlayers={teamOnePlayers} teamTwoPlayers={teamTwoPlayers} matchStatus={matchStatus} />
          </div>
        );
      case "Streamer":
        return (
          <div>
            <MatchesStreamer teamOnePlayers={teamOnePlayers} teamTwoPlayers={teamTwoPlayers} />
          </div>
        );
      case "Table":
        return (
          <div>
            <UpcomingMatchPointsTableTab matchSummary={matchSummary} />
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <>
      <div className="matches-details-wrapper">
        <section className="matches-detail-wrap" aria-label="Match details">
          <div className="matches-detail-data-wrapper">
            {matchStatus === "IN_PROGRESS" && (
              <img
                src={Images.previous_icon}
                alt="back-button"
                onClick={() => {
                  // Check if there's a returnTo in state, otherwise use browser history
                  if (state?.returnTo) {
                    navigate(state.returnTo);
                  } else {
                    // Use browser history to go back to previous page
                    navigate(-1);
                  }
                }}
                className="back-button"
              />
            )}
            {renderHeader()}

            {/* TABS */}
            <nav className="tabs-wrapper" aria-label="Match details tabs">
              <div className="tabs" id="match-tabs" role="tablist" aria-label="Match info sections">
                {TAB_LABELS.map((tab) => {
                  const selected = matchTab === tab;
                  return (
                    <button key={tab} type="button" className={`tab ${selected ? "active" : ""}`} role="tab" aria-selected={selected} tabIndex={selected ? 0 : -1} onClick={() => setMatchTab(tab)}>
                      {tab}
                    </button>
                  );
                })}
              </div>
            </nav>

            {/* TAB PANELS */}
            <main className="tab-content" role="tabpanel">
              {renderContent()}
            </main>
          </div>
        </section>
      </div>
    </>
  );
};

export default MatchesDetails;
